import React, { useEffect, useState } from "react";
import "../assets/css/pages/Event.css";
import "../assets/css/Responsive/Event_Responsive.css";
import { useLocation } from "react-router-dom";
import { fetchPageByDynamicRouting } from "../api/pages";
import PageSections from "../sections/PageSections";
import Lenis from "@studio-freight/lenis"; // 🔥 Lenis import
import { useSeo } from "../contexts/SeoContext";


/**
 * Smooth scroll using Lenis
 * - Same pattern as About / Home / Hours
 * - Runs once on mount
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Disable native smooth scroll to avoid conflicts with Lenis
        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3), // ease-out
            smoothWheel: true,
            smoothTouch: false,
        });

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
        };
    }, []);
};

// Enhanced AOS (Animate On Scroll) hook with stagger support
const useAOS = (sections, loading) => {
    useEffect(() => {
        if (!sections || sections.length === 0 || loading) return;

        let observer = null;
        const observedElements = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.15,
                rootMargin: "0px 0px -100px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (entry.isIntersecting) {
                            const section = entry.target;

                            // Animate the section itself
                            if (!section.classList.contains("aos-animate")) {
                                section.classList.add("aos-animate");
                            }

                            // Trigger stagger animation for all child elements in this section
                            const staggerElements =
                                section.querySelectorAll("[data-stagger-index]");
                            staggerElements.forEach((el, idx) => {
                                // Special handling for lookout-card children - use their data-stagger-index value
                                const isLookoutCardChild =
                                    el.closest(".lookout-card") &&
                                    el !== el.closest(".lookout-card");
                                if (isLookoutCardChild) {
                                    const staggerIndex =
                                        parseInt(
                                            el.getAttribute("data-stagger-index")
                                        ) || 0;
                                    // Wait for card to animate first (600ms) + stagger delay based on index
                                    const delay = 600 + staggerIndex * 150;
                                    setTimeout(() => {
                                        el.classList.add("stagger-animate");
                                    }, delay);
                                } else {
                                    // Regular stagger for other elements
                                    setTimeout(() => {
                                        el.classList.add("stagger-animate");
                                    }, idx * 10);
                                }
                            });
                        }
                    });
                });
            }, observerOptions);

            // Observe all sections with AOS classes
            const sectionsToObserve =
                document.querySelectorAll(".aos-fade-up");
            sectionsToObserve.forEach((element) => {
                if (!element.classList.contains("aos-animate")) {
                    // Check if section is already in view on page load
                    const rect = element.getBoundingClientRect();
                    const isInView =
                        rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

                    if (isInView) {
                        // Section is already visible, trigger animation immediately
                        element.classList.add("aos-animate");
                        const staggerElements =
                            element.querySelectorAll("[data-stagger-index]");
                        staggerElements.forEach((el, idx) => {
                            // Special handling for lookout-card children - use their data-stagger-index value
                            const isLookoutCardChild =
                                el.closest(".lookout-card") &&
                                el !== el.closest(".lookout-card");
                            if (isLookoutCardChild) {
                                const staggerIndex =
                                    parseInt(
                                        el.getAttribute("data-stagger-index")
                                    ) || 0;
                                // Wait for card to animate first (600ms) + stagger delay based on index
                                const delay = 600 + staggerIndex * 150;
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, delay);
                            } else {
                                // Regular stagger for other elements
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, idx * 10);
                            }
                        });
                    } else {
                        // Section not in view, observe it
                        observer.observe(element);
                        observedElements.push(element);
                    }
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            if (observer) {
                observedElements.forEach((element) => {
                    if (element) observer.unobserve(element);
                });
            }
        };
    }, [sections, loading]);
};

export default function Event() {
    const location = useLocation();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setPageSeo } = useSeo();
    
    // Extract slug from route path
    const slug = location.pathname.replace(/^\/+/, "");

    // 🔥 Initialize Lenis smooth scroll (same as About / Home / Hours)
    useLenis();

    // Initialize AOS animations
    useAOS(sections, loading);

    useEffect(() => {
        let isMounted = true;

        const applySections = (payload) => {
            if (!isMounted) return;
            const nextSections = Array.isArray(payload?.page_sections)
                ? payload.page_sections
                : [];
            setSections(nextSections);
            
            // Update SEO data
            if (payload?.seo) {
                setPageSeo(payload.seo);
            }
            
            setError(null);
            setLoading(false);
        };

        const handleError = (message) => {
            if (!isMounted) return;
            setError(message);
            setSections([]);
            setLoading(false);
        };

        const loadPage = async () => {
            setLoading(true);
            try {
                const res = await fetchPageByDynamicRouting(slug);
                applySections(res);
            } catch (err) {
                handleError("Unable to load the page at the moment.");
            } finally {
                setLoading(false);
            }
        };

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [slug, setPageSeo]);

    // Always render page structure - show content immediately when available

    return (
        <div>
            {error && <div className="page-error">{error}</div>}
            {loading ? (
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="page-loading">Loading...</div>
                </div>
            ) : (
                <PageSections sections={sections} animateHero={true} />
            )}
        </div>
    );
}
